/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { forwardRef, useEffect, useRef, useState } from 'react';
import styles from './Carousel.module.scss';
import SharedCarousel from '@/components/Carousel';
import HeroControls from './HeroControls';
import CarouselArrows from './CarouselArrows';
import { HeroContent } from './HeroHeaderContent';
import { Picture } from '@/components/Picture/Picture';
import { useResponsiveImageData } from '@/features/contentstack/hooks/useResponsiveImageData';
import type { ContentStackMegaHero, SizePerDevice } from '@/features/contentstack/interface';
import type { CSSProperties } from 'react';
import { resolveColor } from './utils';

const DEFAULT_ROTATION_SPEED_SECS = 5;

const sizePerDevice: SizePerDevice = {
  mobile: { width: '100%', height: '100%' },
  tablet: { width: '100%', height: '100%' },
  desktop: { width: '100%', height: '100%' },
};

type HeroSlide = ContentStackMegaHero['hero'][number];

type Props = {
  slides: HeroSlide[];
  autoRotationSpeed?: number | null;
  controlsTheme?: HeroSlide['controls_theme'];
  onSlideChange: (index: number) => void;
};

type SlideProps = {
  slide: HeroSlide;
  index: number;
  isActive: boolean;
};

const Slide = forwardRef<HTMLDivElement, SlideProps>(({ slide, index, isActive }, ref) => {
  const imageData = useResponsiveImageData({ image: slide.image, sizePerDevice });
  const legalText = slide.legal?.text || slide.legal?.link_name;
  const legalCopy = slide.legal?.copy;
  const mobileBackground = resolveColor(slide.mobile_background_color);
  const textColor = resolveColor(slide.text_color);

  return (
    <div
      ref={ref}
      className={styles.Slide}
      style={
        mobileBackground ? ({ '--slide-mobile-bg': mobileBackground } as CSSProperties) : undefined
      }
    >
      <div className={styles.ImageWrapper}>
        <Picture
          imageId={`mega-hero-slide-${index}`}
          altText={slide.image?.image_alt_text ?? ''}
          desktop={imageData.desktop}
          tablet={imageData.tablet}
          mobile={imageData.mobile}
          imageClassName={styles.Image}
          priority={index === 0}
        />
      </div>
      {/* inert removes inactive slide content from tab order without affecting animation */}
      <div className={styles.TextOverlay} inert={!isActive || undefined}>
        <HeroContent
          header={slide.heading}
          subtitle={slide.sub_heading}
          buttonText={slide.button?.title}
          buttonTheme={slide.button?.theme}
          link={slide.link}
          legalText={legalText}
          legalCopy={legalCopy}
          textColor={textColor}
        />
      </div>
      {/* Extends the click target to the image/background; the CTA, legal, and
          mobile text link remain independently reachable via pointer-events:auto */}
      {slide.link && (
        <a
          className={styles.StretchedLink}
          href={slide.link}
          aria-hidden="true"
          tabIndex={-1}
          inert={!isActive || undefined}
        />
      )}
    </div>
  );
});
Slide.displayName = 'Slide';

const Carousel = ({ slides, autoRotationSpeed, controlsTheme, onSlideChange }: Props) => {
  const rotationSpeed = (autoRotationSpeed ?? DEFAULT_ROTATION_SPEED_SECS) * 1000;
  const [isPaused, setIsPaused] = useState(false);
  const [isFocusPaused, setIsFocusPaused] = useState(false);
  const [areArrowsVisible, setAreArrowsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const sharedCarouselRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  // Mobile stacked mode: track each slide's natural height so the carousel
  // only ever takes up as much room as the active slide, not the tallest one.
  const [slideHeights, setSlideHeights] = useState<Record<number, number>>({});

  // Reset to '' once consumed (see onImageChange below) so the same direction can re-trigger
  const [slideCommand, setSlideCommand] = useState('');

  // Remove SharedCarousel's built-in tab stops; custom arrows handle keyboard nav
  useEffect(() => {
    if (!sharedCarouselRef.current) {
      return;
    }
    const el = sharedCarouselRef.current;
    el.querySelectorAll<HTMLElement>(
      '[aria-roledescription="carousel"], [role="region"], [aria-roledescription="slide"]',
    ).forEach((node) => {
      node.setAttribute('tabindex', '-1');
    });
  }, [slides.length]);

  useEffect(() => {
    if (!areArrowsVisible) {
      return;
    }
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setAreArrowsVisible(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [areArrowsVisible]);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      setSlideHeights((prev) => {
        const next = { ...prev };
        entries.forEach((entry) => {
          const index = Number((entry.target as HTMLElement).dataset.slideIndex);
          next[index] = entry.contentRect.height;
        });
        return next;
      });
    });
    slideRefs.current.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [slides.length]);

  const handlePrev = () => setSlideCommand('PREV');
  const handleNext = () => setSlideCommand('NEXT');

  const activeSlideHeight = slideHeights[currentSlide];

  return (
    <div
      ref={containerRef}
      className={styles.Carousel}
      style={
        activeSlideHeight
          ? ({ '--mega-hero-active-slide-height': `${activeSlideHeight}px` } as CSSProperties)
          : undefined
      }
      onClick={() => setAreArrowsVisible(true)}
      onFocus={() => {
        setAreArrowsVisible(true);
        setIsFocusPaused(true);
      }}
      onBlur={(e) => {
        if (!containerRef.current?.contains(e.relatedTarget as Node)) {
          setAreArrowsVisible(false);
          setIsFocusPaused(false);
        }
      }}
    >
      {slides.length > 1 && (
        <CarouselArrows onPrev={handlePrev} onNext={handleNext} isVisible={areArrowsVisible} />
      )}
      {/* Mobile: pause before TextLinkMobile */}
      <div className={`${styles.ControlsAnchor} ${styles.ControlsAnchorMobile}`}>
        <HeroControls
          theme={controlsTheme}
          isPaused={isPaused}
          onTogglePause={() => setIsPaused((p) => !p)}
          currentSlide={currentSlide}
          totalSlides={slides.length}
        />
      </div>
      <div ref={sharedCarouselRef} className={styles.SharedCarouselWrapper}>
        <SharedCarousel
          autoRotationEnabled={
            slides.length > 1 && rotationSpeed > 0 && !isPaused && !isFocusPaused
          }
          autoRotationSpeed={rotationSpeed}
          slideToImage={slideCommand}
          onImageChange={(index) => {
            setCurrentSlide(index);
            onSlideChange(index);
            setSlideCommand('');
          }}
          showArrows={false}
          showCarouselDots={false}
          hideThumbnails
          classes={{
            root: styles.SharedCarousel,
            customCarouselContainer: styles.CarouselContainer,
            carouselItemContainer: styles.ImageContainer,
          }}
        >
          {slides.map((slide, index) => (
            <Slide
              key={slide.image?.image_url ?? index}
              ref={(node) => {
                if (node) {
                  node.dataset.slideIndex = String(index);
                  slideRefs.current.set(index, node);
                } else {
                  slideRefs.current.delete(index);
                }
              }}
              slide={slide}
              index={index}
              isActive={index === currentSlide}
            />
          ))}
        </SharedCarousel>
      </div>
      {/* Desktop: pause after slide content */}
      <div className={`${styles.ControlsAnchor} ${styles.ControlsAnchorDesktop}`}>
        <HeroControls
          theme={controlsTheme}
          isPaused={isPaused}
          onTogglePause={() => setIsPaused((p) => !p)}
          currentSlide={currentSlide}
          totalSlides={slides.length}
        />
      </div>
    </div>
  );
};

export default Carousel;
